import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getOnlineFemales, updateUserStatus, getUserProfile, getUserBalance } from '../utils/api';
import PackagesModal from '../components/PackagesModal';
import io from 'socket.io-client';
import './MaleDashboard.css';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';

function MaleDashboard() {
    const { user, logout, updateUser } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [onlineFemales, setOnlineFemales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [socket, setSocket] = useState(null);
    const [showPackagesModal, setShowPackagesModal] = useState(false);
    const [currentUser, setCurrentUser] = useState(user);
    
    // Track initial online females IDs when component first loads
    const previousOnlineIdsRef = useRef(new Set());
    // Track females that were already online when component mounted
    const wasOnlineOnMountRef = useRef(new Set());
    // Track females that have gone offline at least once (to identify reconnected ones)
    const wentOfflineIdsRef = useRef(new Set());

    // Refresh user balance when returning to dashboard (e.g., after a call)
    useEffect(() => {
        const refreshUserBalance = async () => {
            if (!user?.id) return;
            
            try {
                // Get fresh balance from server
                const balanceResponse = await getUserBalance(user.id);
                const balance = balanceResponse.balance || 0;
                
                // Update user object with fresh balance
                const updatedUser = { ...user, balance };
                updateUser(updatedUser);
                setCurrentUser(updatedUser);
                
                console.log('💰 Dashboard: Refreshed user balance to', balance, 'seconds');
            } catch (error) {
                console.error('Error refreshing user balance:', error);
            }
        };

        // Refresh balance when component mounts or when location changes (returning from call)
        refreshUserBalance();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    useEffect(() => {
        // Initialize socket
        const newSocket = io(SOCKET_URL, { auth: { userId: user.id } });
        setSocket(newSocket);

        // Emit user online event
        newSocket.emit('user-online', user.id);

        // Listen for online users updates (real-time)
        newSocket.on('online-users-update', () => {
            console.log('📢 Received online-users-update event, reloading females list...');
            // Reload online females list when update is received (real-time, not initial)
            loadOnlineFemales(false);
        });

        // Set user as online
        updateUserStatus(user.id, true, false);

        // Load online females (initial load)
        loadOnlineFemales(true);

        return () => {
            // IMPORTANT: don't mark offline on route changes (e.g. navigating to VideoCall),
            // otherwise signaling can break mid-call. Disconnect is enough.
            newSocket.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user.id]);

    const loadOnlineFemales = async (isInitialLoad = false) => {
        try {
            console.log('🔄 Loading online females...', isInitialLoad ? '(initial)' : '(update)');
            const females = await getOnlineFemales(user?.id);
            console.log('✅ Online females loaded:', females.length, 'females');
            
            const currentIds = new Set(females.map(f => f._id));
            
            if (isInitialLoad) {
                // First load - store IDs of females that were already online
                const initialIds = new Set(currentIds);
                previousOnlineIdsRef.current = new Set(initialIds);
                wasOnlineOnMountRef.current = new Set(initialIds);
                wentOfflineIdsRef.current.clear();
                console.log('📌 Initial online IDs stored:', Array.from(initialIds));
            } else {
                // Subsequent loads - track who went offline
                const previousIds = previousOnlineIdsRef.current;
                
                // Find females that were online before but are not now (went offline)
                previousIds.forEach(id => {
                    if (!currentIds.has(id)) {
                        wentOfflineIdsRef.current.add(id);
                        console.log('📴 Female went offline:', id);
                    }
                });
                
                // Update previous list with current online females for next comparison
                previousOnlineIdsRef.current = new Set(currentIds);
            }
            
            // Sort females:
            // 1. Initially online (when component mounted) - top
            // 2. New arrivals (not in initial list) - middle  
            // 3. Reconnected (were offline, came back) - bottom
            const sortedFemales = females.sort((a, b) => {
                const aWasInitial = wasOnlineOnMountRef.current.has(a._id);
                const bWasInitial = wasOnlineOnMountRef.current.has(b._id);
                const aWentOffline = wentOfflineIdsRef.current.has(a._id);
                const bWentOffline = wentOfflineIdsRef.current.has(b._id);
                
                // Reconnected (went offline and came back) - bottom
                if (aWentOffline && !bWentOffline) return 1;
                if (!aWentOffline && bWentOffline) return -1;
                
                // Initially online - top
                if (aWasInitial && !bWasInitial) return -1;
                if (!aWasInitial && bWasInitial) return 1;
                
                // Same category - maintain original order
                return 0;
            });
            
            console.log('📋 Sorted females:', sortedFemales.map(f => ({
                id: f._id,
                nickname: f.nickname,
                wasInitial: wasOnlineOnMountRef.current.has(f._id),
                wentOffline: wentOfflineIdsRef.current.has(f._id)
            })));
            
            setOnlineFemales(sortedFemales);
        } catch (error) {
            console.error('❌ Error loading females:', error);
        } finally {
            if (loading) {
                setLoading(false);
            }
        }
    };

    const handleCallFemale = async (femaleId) => {
        try {
            // Find the female in the list to check free trial status
            const female = onlineFemales.find(f => f._id === femaleId);
            const hasFreeTrial = female && !female.freeTrialUsed;
            
            // Check user balance before allowing call
            const balanceResponse = await getUserBalance(user.id);
            const balance = balanceResponse.balance || 0;
            
            // Allow call if user has balance OR free trial is available
            // (Free trial gives 15 seconds free, so even with 0 balance, call is allowed for first time)
            if (balance === 0 && !hasFreeTrial) {
                // No balance and no free trial available, show packages modal
                setShowPackagesModal(true);
                return;
            }
            
            // If balance > 0 or free trial available, allow the call
            navigate(`/video-call/${femaleId}`);
        } catch (error) {
            console.error('Error checking balance:', error);
            // On error, still allow call (server will handle it)
            navigate(`/video-call/${femaleId}`);
        }
    };

    const handleLogout = async () => {
        // Set user offline before logout
        try {
            await updateUserStatus(user.id, false, false);
            if (socket) {
                socket.emit('user-offline', user.id);
                socket.disconnect();
            }
        } catch (error) {
            console.error('Error setting user offline:', error);
        }
        logout();
        navigate('/');
    };

    const handlePurchaseSuccess = async (newBalance) => {
        // Refresh user data
        try {
            const updatedUser = await getUserProfile(user.id);
            updateUser(updatedUser);
            setCurrentUser(updatedUser);
        } catch (error) {
            console.error('Error refreshing user:', error);
        }
    };

    return (
        <div className="male-dashboard">
            <header className="dashboard-header">
                <div className="header-content">
                    <h1>Next</h1>
                    <div className="header-actions">
                        <div className="user-info">
                            <span className="nickname">{user?.nickname}</span>
                            <span className="balance">
                                Balance: {Math.floor((currentUser?.balance || user?.balance || 0) / 60)} min
                            </span>
                        </div>
                        <button 
                            className="btn-packages"
                            onClick={() => setShowPackagesModal(true)}
                        >
                            Buy Minutes
                        </button>
                        <button 
                            className="btn-edit-profile" 
                            onClick={() => navigate('/edit-profile')}
                        >
                            Edit Profile
                        </button>
                        <button className="btn-logout" onClick={handleLogout}>Logout</button>
                    </div>
                </div>
            </header>

            <main className="dashboard-main">
                <div className="models-section">
                    <h2>Online Models</h2>
                    
                    {loading ? (
                        <div className="loading-spinner">Loading...</div>
                    ) : onlineFemales.length === 0 ? (
                        <div className="no-models">
                            <p>No models are currently online.</p>
                            <p>Please check back later.</p>
                        </div>
                    ) : (
                        <div className="models-grid">
                            {onlineFemales.map(female => (
                                <div key={female._id} className="model-card">
                                    <div className="model-photo">
                                        <img 
                                            src={`${process.env.REACT_APP_API_URL?.replace('/api', '')}/${female.profilePhoto}`} 
                                            alt={female.nickname}
                                            onError={(e) => {
                                                e.target.src = 'https://via.placeholder.com/200';
                                            }}
                                        />
                                        {female.isBusy && (
                                            <div className="busy-badge">Busy</div>
                                        )}
                                        {!female.isBusy && (
                                            <div className="online-badge">
                                                <span className="status-dot"></span>
                                                Online
                                            </div>
                                        )}
                                    </div>
                                    <div className="model-info">
                                        <h3>{female.nickname}</h3>
                                        <button 
                                            className={`btn-call ${female.isBusy ? 'disabled' : ''}`}
                                            onClick={() => handleCallFemale(female._id)}
                                            disabled={female.isBusy}
                                        >
                                            {female.isBusy ? 'Busy' : 'Start Call'}
                                        </button>
                                        <p className="free-trial">
                                            {female.freeTrialUsed 
                                                ? 'Call to chat' 
                                                : 'First 15 seconds free!'}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </main>

            <PackagesModal
                isOpen={showPackagesModal}
                onClose={() => setShowPackagesModal(false)}
                onPurchaseSuccess={handlePurchaseSuccess}
            />
        </div>
    );
}

export default MaleDashboard;
